import React, { useState, useEffect, useRef } from 'react';
import Header from '../../components/ui/Header';
import ConversationSidebar from './components/ConversationSidebar';
import ChatArea from './components/ChatArea';
import ProjectContextHeader from './components/ProjectContextHeader';

const BuyerVendorMessagingInterface = () => {
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Mock data for conversations - to be replaced with Supabase data later
  const [conversations] = useState([
  {
    id: 1,
    vendorName: 'HomeReno Experts',
    vendorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_11bb9f0ec-1769443975240.png",
    projectTitle: 'Kitchen Renovation & Remodeling',
    lastMessage: 'I can complete your kitchen renovation within 4 weeks including custom cabinetry and countertops.',
    lastMessageTime: '2 min ago',
    unreadCount: 2,
    isOnline: true,
    messages: [
    {
      id: 1,
      senderId: 'vendor_1',
      senderName: 'John Smith',
      senderType: 'vendor',
      content: 'Hello! Thank you for considering our services for your kitchen renovation project.',
      timestamp: '2025-11-16T14:30:00Z',
      status: 'delivered'
    },
    {
      id: 2,
      senderId: 'buyer_1',
      senderName: 'You',
      senderType: 'buyer',
      content: 'Hi John, I reviewed your portfolio and I\'m impressed. Can you tell me more about your approach to kitchen remodeling?',
      timestamp: '2025-11-16T14:35:00Z',
      status: 'delivered'
    },
    {
      id: 3,
      senderId: 'vendor_1',
      senderName: 'John Smith',
      senderType: 'vendor',
      content: 'Absolutely! I would start with a detailed assessment of your space and preferences. Then create 3D designs and material selections before construction begins.',
      timestamp: '2025-11-16T14:40:00Z',
      status: 'delivered'
    },
    {
      id: 4,
      senderId: 'vendor_1',
      senderName: 'John Smith',
      senderType: 'vendor',
      content: 'I can complete your kitchen renovation within 4 weeks including custom cabinetry and countertops.',
      timestamp: '2025-11-16T15:20:00Z',
      status: 'delivered'
    }]

  },
  {
    id: 2,
    vendorName: 'GreenScape Landscaping',
    vendorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_15f77fa05-1769443975340.png",
    projectTitle: 'Backyard Landscaping & Garden Design',
    lastMessage: 'I\'ve prepared some initial design concepts for your outdoor space.',
    lastMessageTime: '1 hour ago',
    unreadCount: 0,
    isOnline: false,
    messages: [
    {
      id: 1,
      senderId: 'vendor_2',
      senderName: 'Sarah Johnson',
      senderType: 'vendor',
      content: 'Hi! I\'m excited to work on your backyard landscaping project.',
      timestamp: '2025-11-16T13:15:00Z',
      status: 'delivered'
    },
    {
      id: 2,
      senderId: 'vendor_2',
      senderName: 'Sarah Johnson',
      senderType: 'vendor',
      content: 'I\'ve prepared some initial design concepts for your outdoor space.',
      timestamp: '2025-11-16T14:20:00Z',
      status: 'delivered'
    }]

  },
  {
    id: 3,
    vendorName: 'Bathroom Pro Specialists',
    vendorImage: "https://images.unsplash.com/photo-1648631478953-1334ce24a24c",
    projectTitle: 'Master Bathroom Renovation',
    lastMessage: 'When would be a good time to schedule our in-home consultation?',
    lastMessageTime: '3 hours ago',
    unreadCount: 1,
    isOnline: true,
    messages: [
    {
      id: 1,
      senderId: 'vendor_3',
      senderName: 'Mike Chen',
      senderType: 'vendor',
      content: 'Thank you for reaching out! I\'d love to discuss your bathroom renovation needs.',
      timestamp: '2025-11-16T12:20:00Z',
      status: 'delivered'
    },
    {
      id: 2,
      senderId: 'vendor_3',
      senderName: 'Mike Chen',
      senderType: 'vendor',
      content: 'When would be a good time to schedule our in-home consultation?',
      timestamp: '2025-11-16T12:25:00Z',
      status: 'delivered'
    }]

  }]
  );

  // Filter conversations based on search query
  const filteredConversations = conversations?.filter((conversation) =>
  conversation?.vendorName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
  conversation?.projectTitle?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
  conversation?.lastMessage?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  // Set initial selected conversation
  useEffect(() => {
    if (conversations?.length > 0 && !selectedConversation) {
      setSelectedConversation(conversations?.[0]);
    }
  }, [conversations, selectedConversation]);

  const handleConversationSelect = (conversation) => {
    setSelectedConversation(conversation);
    // Mark messages as read (to be implemented with Supabase later)
  };

  const handleSendMessage = (messageContent, attachments = []) => {
    if (!selectedConversation || !messageContent?.trim()) return;

    const newMessage = {
      id: Date.now(),
      senderId: 'buyer_1',
      senderName: 'You',
      senderType: 'buyer',
      content: messageContent,
      attachments: attachments,
      timestamp: new Date()?.toISOString(),
      status: 'sending'
    };

    // Add message to current conversation (to be replaced with Supabase real-time)
    setSelectedConversation((prev) => ({
      ...prev,
      messages: [...prev?.messages, newMessage]
    }));

    // Simulate message delivery
    setTimeout(() => {
      setSelectedConversation((prev) => ({
        ...prev,
        messages: prev?.messages?.map((msg) =>
        msg?.id === newMessage?.id ? { ...msg, status: 'delivered' } : msg
        )
      }));
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="flex h-screen pt-32">
        {/* Conversation Sidebar */}
        <div className={`${isSidebarOpen ? 'w-80' : 'w-0'} transition-all duration-300 overflow-hidden border-r border-border bg-background`}>
          <ConversationSidebar
            conversations={filteredConversations}
            selectedConversation={selectedConversation}
            onConversationSelect={handleConversationSelect}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            isOpen={isSidebarOpen} />

        </div>

        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col">
          {selectedConversation ?
          <>
              <ProjectContextHeader
              conversation={selectedConversation}
              onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
              isSidebarOpen={isSidebarOpen} />

              <ChatArea
              conversation={selectedConversation}
              onSendMessage={handleSendMessage} />

            </> :

          <div className="flex-1 flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">💬</span>
                </div>
                <h3 className="text-lg font-medium mb-2">Select a conversation</h3>
                <p className="text-sm">Choose a conversation from the sidebar to start messaging</p>
              </div>
            </div>
          }
        </div>

        {/* Mobile Sidebar Overlay */}
        {isSidebarOpen &&
        <div
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsSidebarOpen(false)} />

        }
      </div>
    </div>);

};

export default BuyerVendorMessagingInterface;